<template>
  <section class="main">
    <h1 class="intro">
      {{ $t('MY_ALTSTORE_REPOSITORIES_HEADING') }}
    </h1>
    <p class="instruction" v-if="repos.length">
      {{ $t("MY_REPOS_MOBILE_INSTRUCTION") }}
    </p>
    <div class="row">
      <loader v-if="gettingRepos" />
      <template v-else>
        <div class="items">
          <div v-if="!repos.length">
            {{ $t('NO_REPOS_FOUND') }}
          </div>
          <div
            class="item item_active"
            v-for="repo in repos"
            :class="{
              item_disabled: repoRemovingId === repo.id,
            }"
            :key="repo.id"
            @click="browseRepoMobile(repo.id, $event)"
          >
            <div class="item__desc">
              <span class="item__title">
                {{ repo.name }} ({{ +repo.is_public ? $t('PUBLIC_REPO') : $t('PRIVATE_REPO') }})
                <a
                  href="#"
                  class="item__remove"
                  ref="removeRepo"
                  @click.prevent="removeRepo(repo.id)">
                  {{
                    repoRemovingId === repo.id
                      ? $t("APP_REMOVING")
                      : $t("REMOVE")
                  }}
                </a>
              </span>
              <span class="item__info">
                {{ $t('STATUS_IS_X', {status: repo.status_translated}) }}
                <br/>
                {{ $t('APPS_N', {apps: repo.total_apps}) }}
                <br/>
                {{ $t('LAST_CHECKED_AT',
                  {date: new Date(repo.last_checked_at * 1000).toLocaleString()}) }}
              </span>
            </div>
            <div class="item__switch">
              <router-link class="item__button item__button_desktop" :to="`/repos/${repo.id}`">
                {{ $t("BROWSE") }}
              </router-link>
              <button
                ref="toggleRepoPublicity"
                class="item__button"
                @click.prevent="toggleRepoPublicity(repo.id)"
              >
                <loader
                  v-if="repoPublicityChangingId === repo.id" size="15px" border="3px" absolute
                />
                {{ +repo.is_public ? $t('MAKE_REPO_PRIVATE') : $t('SHARE_REPO') }}
              </button>
            </div>
          </div>
        </div>
        <div class="link">
          <html-with-links
            tag="p"
            class="link__text"
            :html="$t('SEARCHING_FOR_PUBLIC_REPOS', {href: '/repos'})"
          />
          <p class="link__text">
            {{ $t("MY_ALTSTORE_REPOSITORIES_SIDEBAR") }}
          </p>
          <v-btn type="primary" @click.native="tryAddRepository">{{ $t('ADD_REPOSITORY') }}</v-btn>
          <p class="link__text" v-html="$t('MY_ALTSTORE_REPOSITORIES_ADD_YOURS',
            {'href': origin + '/repos/import?url=YOUR_REPOSITORY_JSON_FILE_URL'}
          )"></p>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import isItselfLinkable from '@/utils/isItselfLinkable';
import VBtn from '@/components/Button.vue';
import Loader from '@/components/Loader.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: { VBtn, Loader, HtmlWithLinks },
  data() {
    return {
      origin: window.location.origin,
      code: '',
      isMobile: isItselfLinkable(),
      deviceRemoving: null,
      repoRemovingId: null,
      repoPublicityChangingId: null,
      gettingRepos: true,
      repos: [],
    };
  },
  computed: {
    title() {
      return this.$t('MY_ALTSTORE_REPOSITORIES');
    },
  },
  methods: {
    tryAddRepository() {
      this.$prompt(this.$t('ENTER_REPOSITORY_URL'), '', { validate: this.validateRepoURL })
        .then((url) => {
          this.$router.push(`/repos/import?url=${encodeURIComponent(url)}`);
        });
    },
    validateRepoURL(url) {
      try {
        // eslint-disable-next-line no-new
        new URL(url);

        return true;
      } catch {
        this.$notify({
          type: 'error',
          text: this.$t('PROVIDE_VALID_URL'),
        });

        return false;
      }
    },
    browseRepo(id) {
      this.$router.push(`/repos/${id}`);
    },
    removeRepo(id) {
      if (this.repoRemovingId) {
        return;
      }

      this.repoRemovingId = id;
      this.$api.deleteAltstoreRepo(id)
        .then(() => {
          const idx = this.repos.findIndex(repo => repo.id === id);
          this.repos.splice(idx, 1);
        })
        .finally(() => {
          this.repoRemovingId = null;
        });
    },
    async toggleRepoPublicity(id) {
      if (this.repoPublicityChangingId) {
        return;
      }

      const repo = this.repos.find(r => r.id === id);
      if (+repo.is_public) {
        this.repoPublicityChangingId = id;
        await this.$api.editAltstoreRepo({ id, url: repo.url, isPublic: 0 })
          .finally(() => {
            this.repoPublicityChangingId = null;
          });
        repo.is_public = 0;
        return;
      }

      try {
        await this.$confirm(this.$t('MAKE_REPO_PUBLIC_CONFIRM'));
        this.repoPublicityChangingId = id;
        await this.$api.editAltstoreRepo({ id, url: repo.url, isPublic: 1 });
        repo.is_public = 1;
      } finally {
        this.repoPublicityChangingId = null;
      }
    },
    browseRepoMobile(id, e) {
      if (window.innerWidth >= 1250) {
        return;
      }
      const isClickOnButton = [this.$refs.removeRepo, this.$refs.toggleRepoPublicity]
        .flat().some(el => el === e.target);
      if (isClickOnButton) {
        return;
      }
      this.$router.push(`/repos/${id}`);
    },
    getRepos() {
      this.gettingRepos = true;
      return this.$api.getAltstoreRepos()
        .then((response) => {
          this.repos = response;
        })
        .finally(() => {
          this.gettingRepos = false;
        });
    },
  },
  created() {
    this.getRepos();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;
}

.instruction {
  display: none;

  @include notebook {
    display: block;
    font-size: 12px;
    margin-bottom: 15px;
    padding: 0 10px;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-direction: column;
    align-items: stretch;
  }
}

.items {
  flex-grow: 1;
}

.item {
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 4px;
  margin-bottom: 15px;
  @include transition;

  @include notebook {
    display: block;
  }

  &:not(:first-child) {
    margin-top: 5px;
  }

  &_active {
    box-shadow: var(--my-device-shadow);
    border-color: transparent;
  }

  &_disabled {
    pointer-events: none;
  }

  &__img-wrapper {
    flex: 0 0 44px;
    text-align: center;
    margin-right: 15px;
  }

  &__img {
    height: 45px;
    filter: var(--img-filter);
  }

  &__title {
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  &__pro {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__remove {
    color: #fa6565;
    margin-left: 5px;
  }

  &__desc {
    flex-grow: 1;
  }

  &__info {
    line-height: 21px;
  }

  &__jail {
    white-space: nowrap;
    border-radius: 2.5px;
    font-size: 12px;
    padding: 1px 3px 2px;
    line-height: 10px;
    position: relative;
    top: -1px;
    margin-left: 2px;
    background: var(--tertiary-accent);
    color: var(--text-tertiary-accent);

    &_active {
      background: var(--bg-success);
      color: #053305;
    }
  }

  &__switch {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @include notebook {
      align-items: flex-start;
      margin-top: 5px;
    }
  }

  &__button {
    position: relative;
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    color: var(--text-primary);
    font-size: 12px;
    padding: 3px 15px;

    &_desktop {
      @include notebook {
        display: none;
      }
    }
  }

  &__switch-text {
    color: $text-secondary;
    font-size: 12px;
    margin-top: 2px;
  }
}

.link {
  width: 200px;
  margin-left: 50px;
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;

  @include notebook {
    margin: 15px auto 0;
    width: 100%;
    max-width: 400px;
  }

  &__title {
    font-weight: 500;
    margin-bottom: 10px;
    text-align: left;
  }

  &__text {
    line-height: 15px;
    text-align: left;
    margin-bottom: 15px;
    word-break: break-word;

    &:last-child {
      margin-bottom: 0;
      margin-top: 15px;
    }

    &::v-deep {
      b {
        word-break: break-all;
        font-weight: 700;
      }
    }
  }

  &__code {
    display: block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 5px;
    margin: 10px 0 5px;
  }

  &__qr {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  &__list {
    list-style: decimal inside;
    text-align: left;

    &::v-deep {
      li {
        margin-bottom: 3px;
        line-height: 18px;
      }

      strong {
        font-weight: bold;
      }
    }
  }
}
</style>
